import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import {
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
  makeStyles,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import API from "../../services/api";

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.

const stripePromise = loadStripe(API.getPlaidPublicKey());
const useStyles = makeStyles((theme) => ({
  textField: {
    "&.MuiTextField-root": {
      borderBottom: "solid black 1px",
      margin: 0,
      "&.MuiInput-root": {},
    },
  },
  inputsContainer: {
    border: "solid black 1px",
    borderRadius: 10,
  },
  errorMessage: {
    marginTop: 5,
    color: theme.palette.error.light,
  },
}));

const CheckoutForm = ({ period }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [disabled, setDisabled] = useState(true);
  const [clientSecret, setClientSecret] = useState("");

  const user = useSelector((state) => state.firebase.profile);

  const classes = useStyles();

  const email = useSelector(
    (state) =>
      state.firebase && state.firebase.auth && state.firebase.auth.email
  );

  const handleChange = async (event) => {
    // Listen for changes in the CardElement
    // and display any errors as the customer types their card details
    setDisabled(event.empty);
    setError(event.error);
  };
  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setProcessing(true);

    const result = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
      billing_details: {
        email,
      },
    });

    await stripePaymentMethodHandler(result);
    setProcessing(false);
  };
  const stripePaymentMethodHandler = async (result) => {
    if (result.error) {
      // Show error in payment form
      setError(result.error);
      return;
    }
    const { customer, subscription, error } = await API.createAndChargeCustomer(
      {
        email,
        payment_method: result.paymentMethod.id,
        period,
      }
    );
    if (error) {
      // Show error in payment form
      setError(error);
      return;
    }
    // The customer has been created
    const { latest_invoice } = subscription;
    const { payment_intent } = latest_invoice;

    const { client_secret, status } = payment_intent;
    if (status === "requires_action") {
      const result = await stripe.confirmCardPayment(client_secret);
      if (result.error) {
        // Display error message in your UI.
        // The card was declined (i.e. insufficient funds, card has expired, etc)
        setError(result.error);
      } else {
        handleSuccess();
      }
    } else {
      handleSuccess();
    }
    return;
  };
  const handleSuccess = () => {
    setSucceeded(true);
  };
  return (
    <>
      {user && user.subscription && user.subscription.status === "active" && (
        <Redirect to="/a/settings" />
      )}
      <form>
        <div className={classes.inputsContainer}>
          <Tooltip title="You must use your account email">
            <TextField
              className={classes.textField}
              value={email}
              fullWidth
              disabled
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">Email</InputAdornment>
                ),
                style: { padding: "4px 8px 0" },
              }}
            />
          </Tooltip>
          <div
            style={{
              padding: 8,
            }}
          >
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
              }}
              onChange={handleChange}
            />
          </div>
        </div>
        {error && (
          <Typography className={classes.errorMessage}>
            {error.message || error.raw.message}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          type="submit"
          style={{ marginTop: 10 }}
          onClick={handleSubmit}
          disabled={disabled || !!error || processing || succeeded}
        >
          Upgrade now
          {processing && (
            <CircularProgress style={{ position: "absolute" }} size={24} />
          )}
        </Button>

        <Snackbar open={succeeded} autoHideDuration={6000}>
          <Alert variant="filled" severity="success" elevation={6}>
            You are now subscribed!
          </Alert>
        </Snackbar>
      </form>
    </>
  );
};

export default (props) => (
  <Elements stripe={stripePromise}>
    <CheckoutForm {...props} />
  </Elements>
);
